import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox, DatePicker, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { RadioGroup } from '../../components/controls/Radio';
import { line, Size, centsParam, dayParam, mailAccountingFilePath, oldestFirst, t, documentName } from '@bms2/shared';
import { ModalField } from '../../components/modals/ModalShell';
import { BANK_ACCOUNTS_PATH } from '../banking/api';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { PAYMENTS_PATH, post, useApi } from './api';
import { useCompanySearch } from '../settings/api';
import lineStyles from './DocumentPage.module.css';
import { Money } from '../../components/Money';
import { useAddNew } from './AddNewHost';
import { useDocumentToday } from '../../shell/ViewerZone';

/** What the form opens on: the account, and that account's own open invoices with the balance each still carries. */
export interface PaymentDefaults {
  companyId: string;
  companyName: string;
  currencyCode: string;
  direction: 'Received' | 'Sent';
  paymentDate: string;
  openInvoices: { id: string; invoiceNumber: string | null; invoiceDate: string; dueDate: string; totalCents: number; openBalanceCents: number; status: string }[];
  /** 🔒 The account's credit notes still holding credit (decision 2100); absent from an older reply. */
  heldCredits?: { creditNoteId: string; creditNoteNumber: string; creditDate: string; heldCents: number }[];
}

/** What the bank-account field offers: the accounts the money can leave from, named as the Banking page names them. */
interface PayingAccountRow {
  id: string;
  name: string;
  institution: string | null;
  accountNumberLast4: string | null;
  /** 🔒 The ledger account the receipt's own posting names (decision 0434); a bank account is the name a person knows it by. */
  glAccountId: string;
  isActive: boolean;
}

/** The five rails the record carries; the labels are the enum's own words, so nothing is invented here. */
const METHODS = ['Check', 'ACH', 'Card', 'Cash', 'Other'] as const;

/**
 * 🔒 The Payment's edit mode (decision 0409), on the payment's own page rather
 * than in a Record Payment modal. The header takes the account, the direction,
 * the date, the rail, the reference and the amount, with the card surcharge
 * beside it where the rail is a card; the line block is the account's own open
 * invoices with an amount to apply against each.
 *
 * 🔨 The line block is not the Item Table: a payment names no item, and what it
 * applies to is an invoice that already exists.
 *
 * 🔨 The footer carries one control and Cancel, and no Save as Draft. A payment
 * is Recorded the moment it is saved, so a draft would name a state nothing
 * writes and a second control doing the one act.
 */
export function PaymentForm({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const companyFromUrl = params.get('companyId');
  const invoiceFromUrl = params.get('invoiceId');
  // 🔒 T346 (decision 1515): opened from a message in the accounting mailbox, the form carries the amount and the day the remittance read, and files the message once it saves.
  const fromMessage = params.get('fromMessage') || null;
  const amountFromUrl = centsParam(params.get('amountCents'));
  const dateFromUrl = dayParam(params.get('paymentDate'));
  const [account, setAccount] = useState<{ id: string; label: string } | null>(null);
  const query = invoiceFromUrl
    ? `invoiceId=${encodeURIComponent(invoiceFromUrl)}`
    : companyFromUrl || account?.id
    ? `companyId=${encodeURIComponent(companyFromUrl || account!.id)}`
    : null;
  const defaults = useApi<PaymentDefaults>(query ? `${PAYMENTS_PATH}/defaults?${query}` : null, apiBaseUrl);
  const answer = defaults.state.kind === 'ready' ? defaults.state.data : null;
  const asksForAccount = !companyFromUrl && !invoiceFromUrl;
  const readFailed = defaults.state.kind === 'failed';

  // 🔒 + Record payment opens on the direction of the list it was pressed from (decision 0762; T123).
  const directionFromUrl = params.get('direction') === 'Sent' ? 'Sent' : params.get('direction') === 'Received' ? 'Received' : null;
  const [direction, setDirection] = useState<'Received' | 'Sent'>(directionFromUrl ?? 'Received');
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [paymentDate, setPaymentDate] = useState(today);
  const [method, setMethod] = useState<string>('Check');
  const [reference, setReference] = useState('');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  /** 🔒 The account a payment sent left from (decision 0434); a payment received names none and reaches one through its Deposit. */
  const [bankAccountId, setBankAccountId] = useState('');
  /** 🔒 The ledger account a receipt's posting names (decision 0434), offered by the bank account's own name. */
  const [receiptAccountId, setReceiptAccountId] = useState('');
  const [surchargeCents, setSurchargeCents] = useState<number | null>(null);
  const [proRata, setProRata] = useState(false);
  const [applied, setApplied] = useState<Record<string, number>>({});
  /** 🔒 How much of each credit note's held credit to use (decision 2100); every box opens empty, so no credit is used unless somebody types it. */
  const [creditUsed, setCreditUsed] = useState<Record<string, number>>({});
  /**
   * 🔒 Opened from the Payments list, the amount applies to the account's open
   * invoices oldest first until somebody types a figure of their own (T132;
   * decision 0710). Opened from an invoice, the money is already pointed at
   * that invoice and nothing is spread.
   */
  const [autoApply, setAutoApply] = useState(!invoiceFromUrl);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [seen, setSeen] = useState<PaymentDefaults | null>(null);
  if (answer && seen !== answer) {
    setSeen(answer);
    setPaymentDate(dateFromUrl ?? today);
    // The account's own default answers only where the list the form was opened from named no direction.
    if (!directionFromUrl) setDirection(answer.direction);
    // Opened from an invoice, the money is already pointed at it: the balance is offered filled in.
    const named = invoiceFromUrl ? answer.openInvoices.find((row) => row.id === invoiceFromUrl) : undefined;
    if (named) {
      // 🔨 A remittance's own figure is what arrived; the invoice takes as much of it as its balance holds.
      const arrived = amountFromUrl ?? named.openBalanceCents;
      setApplied({ [named.id]: Math.min(arrived, named.openBalanceCents) });
      setAmountCents(arrived);
    } else if (amountFromUrl !== null) setAmountCents(amountFromUrl);
  }

  const currencyCode = answer?.currencyCode ?? 'USD';
  const heldCredits = answer?.heldCredits ?? [];
  // 🔨 Held credit settles the account's own invoices beside money received, and the pro-rata split is the server's alone, so neither side offers it.
  const offersCredit = direction === 'Received' && !proRata && heldCredits.length > 0;
  const creditTotal = offersCredit ? heldCredits.reduce((sum, row) => sum + (creditUsed[row.creditNoteId] ?? 0), 0) : 0;
  // 🔨 The list arrives oldest first; a written-off or a disputed invoice is not spread onto, because each is a person's own act to settle.
  const spreadable = (answer?.openInvoices ?? []).filter((row) => row.status !== 'WrittenOff' && row.status !== 'Disputed');
  const shown: Record<string, number> = autoApply
    ? Object.fromEntries(oldestFirst((amountCents ?? 0) + creditTotal, spreadable.map((row) => ({ id: row.id, openBalance: row.openBalanceCents }))).map((row) => [row.id, row.amount]))
    : applied;
  const appliedTotal = Object.values(shown).reduce((sum, value) => sum + value, 0);
  // 🔒 The invoices take the credit first (decision 2100), so the money left unapplied is what the credit did not cover.
  const unapplied = Math.max(0, (amountCents ?? 0) - Math.max(0, appliedTotal - creditTotal));
  /**
   * 🔨 The account's invoices are receivables, so a payment sent never settles
   * one and none is offered beside that direction (found in review,
   * 2026-09-15). The server refuses the pair by name as well; this is the
   * screen not offering a combination the data model has no answer for.
   */
  const settlesInvoices = direction === 'Received';

  /**
   * 🔨 The list is read whichever way the money moves, so flipping the
   * direction draws the field rather than starting a fetch a person waits on.
   */
  const bankAccounts = useApi<{ rows: PayingAccountRow[] }>(BANK_ACCOUNTS_PATH, apiBaseUrl);
  const payingAccounts = bankAccounts.state.kind === 'ready' ? bankAccounts.state.data.rows : [];
  const accountLabel = (row: PayingAccountRow): string => line(row.name, row.institution, row.accountNumberLast4 ? `····${row.accountNumberLast4}` : null);
  /**
   * 🔒 Where the money left from or lands (decision 0434; a closed account is in neither, decision 8840): each active
   * bank account's own ledger account, offered by the bank account's name,
   * because that is the name a person knows the money by. 1000 Cash —
   * Operating stands in only where no bank account exists, and the field is
   * not drawn at all then.
   */
  const receiptAccounts = payingAccounts.filter((row) => row.isActive);
  const accountSearch = useCompanySearch(apiBaseUrl);

  const save = async (): Promise<void> => {
    if (!answer) return setError(t('payment.new.chooseAccount'));
    const arrived = amountCents ?? 0;
    if (arrived <= 0 && creditTotal <= 0) return setError(t(offersCredit ? 'payment.heldCredit.needsSomething' : 'payment.new.needsAmount'));
    const allocations = settlesInvoices
      ? Object.entries(shown)
        .filter(([, value]) => value > 0)
        .map(([invoiceId, value]) => ({ invoiceId, amountCents: value }))
      : [];
    // 🔨 The over-application is caught here as well as at the server, so the person is told before the money moves.
    const allocatedTotal = allocations.reduce((sum, a) => sum + a.amountCents, 0);
    if (!proRata && allocatedTotal > arrived + creditTotal) return setError(t(creditTotal > 0 ? 'payment.heldCredit.overApplied' : 'payment.new.overApplied'));
    // 🔨 A credit used is a credit applied to an invoice; what the invoices do not take stays held, and the server refuses the same.
    if (creditTotal > allocatedTotal) return setError(t('payment.heldCredit.overUsed'));
    const heldCreditsUsed = heldCredits.filter((row) => (creditUsed[row.creditNoteId] ?? 0) > 0).map((row) => ({ creditNoteId: row.creditNoteId, amountCents: creditUsed[row.creditNoteId]! }));
    setBusy(true);
    // 🔒 Credit used with no money typed records no payment: the credit is applied on its own, and the first invoice it reached opens (decision 2100).
    if (arrived <= 0) {
      const usedAlone = await post<{ applications: { invoiceId: string }[] }>(`${PAYMENTS_PATH}/held-credit`, { companyId: answer.companyId, paymentDate: paymentDate || null, allocations, heldCredits: heldCreditsUsed }, apiBaseUrl);
      setBusy(false);
      if (!usedAlone.ok) return setError(usedAlone.message);
      navigate(`/invoices/${usedAlone.data.applications[0]?.invoiceId ?? allocations[0]!.invoiceId}`);
      return;
    }
    const body = {
      companyId: answer.companyId,
      direction,
      paymentDate: paymentDate || null,
      method,
      reference: reference.trim() || null,
      amountCents,
      surchargeAmountCents: method === 'Card' ? surchargeCents : null,
      // 🔨 Sent only: the server refuses the pair on the other side, and the field is not drawn there (decision 0434).
      bankAccountId: direction === 'Sent' ? bankAccountId || null : null,
      // 🔒 The receipt's own ledger account (decision 0434); blank leaves the server on 1000 Cash — Operating.
      bankAccountGlId: direction === 'Received' ? receiptAccountId || null : null,
      ...(settlesInvoices && proRata ? { proRataAcrossOpenInvoices: true } : { allocations }),
      ...(heldCreditsUsed.length > 0 ? { heldCredits: heldCreditsUsed } : {}),
    };
    const saved = await post<{ id: string }>(PAYMENTS_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    // 🔒 The message the form was opened from is filed against the new Payment, which marks it dealt with (decision 1515).
    if (fromMessage) await post(mailAccountingFilePath(fromMessage), { recordType: 'Payment', recordId: saved.data.id }, apiBaseUrl);
    navigate(`/payments/${saved.data.id}?direction=${direction}`);
  };

  return (
    <>
    <DocumentEditPage
      title={t('payment.new.title')}
      subtitle={answer?.companyName ?? account?.label ?? documentName('Payment')}
      outwardLabel={t('payment.new.record')}
      busy={busy}
      error={error ?? (readFailed ? t('payment.new.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveAndSend={() => void save()}
      onCancel={() => navigate(directionFromUrl ? `/payments?direction=${directionFromUrl}` : '/payments')}
      header={
        <>
          {asksForAccount ? (
            <div data-payment-field="account" data-payment-account={answer?.companyId ?? ''}>
              <Dropdown size={Size.Medium} label={t('payment.new.account')} entityLabel={t('payment.new.accountEntity')} search={accountSearch} value={account?.label ?? null} onSelect={(row) => setAccount({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord' }, (row) => setAccount({ id: row.id, label: row.line1 }))} required />
            </div>
          ) : (
            <ModalField label={t('payment.new.account')}>
              <span data-payment-account={answer?.companyId ?? ''}>{answer?.companyName ?? ''}</span>
            </ModalField>
          )}
          <RadioGroup
            name="paymentDirection"
            legend={t('payment.new.direction')}
            orientation="horizontal"
            value={direction}
            onChange={(value) => setDirection(value === 'Sent' ? 'Sent' : 'Received')}
            options={[{ value: 'Received', label: t('payment.new.directionReceived') }, { value: 'Sent', label: t('payment.new.directionSent') }]}
          />
          <DatePicker size={Size.Small} label={t('payment.new.paymentDate')} value={paymentDate} onChange={setPaymentDate} mode="date" name="paymentDate" />
          <Select size={Size.Medium} label={t('payment.new.method')} value={method} onChange={setMethod} options={METHODS.map((row) => ({ value: row, label: row }))} dataAttribute={{ 'data-payment-method': 'true' }} required />
          {/* 🔒 Where the money left from, on the side that has an answer: a receipt is banked by a Deposit, and that is what names its account (decision 0434). */}
          {direction === 'Sent' ? (
            <div data-payment-field="bankAccount">
              <Select size={Size.Medium}
                label={t('payment.new.bankAccount')}
                value={bankAccountId}
                onChange={setBankAccountId}
                options={receiptAccounts.map((row) => ({ value: row.id, label: accountLabel(row) }))}
                placeholder={t('payment.new.bankAccountNone')}
                hint={t('payment.new.bankAccountHint')}
                dataAttribute={{ 'data-payment-bank-account': 'true' }}
              />
            </div>
          ) : receiptAccounts.length > 0 ? (
            <div data-payment-field="receiptAccount">
              <Select size={Size.Medium}
                label={t('payment.new.receiptAccount')}
                value={receiptAccountId}
                onChange={setReceiptAccountId}
                options={receiptAccounts.map((row) => ({ value: row.glAccountId, label: accountLabel(row) }))}
                placeholder={t('payment.new.receiptAccountDefault')}
                hint={t('payment.new.receiptAccountHint')}
                dataAttribute={{ 'data-payment-receipt-account': 'true' }}
              />
            </div>
          ) : (
            <p className={styles.hint} data-payment-bank-account-received="true">{t('payment.new.bankAccountReceived')}</p>
          )}
          <TextInput size={Size.Medium} label={t('payment.new.reference')} value={reference} onChange={setReference} name="paymentReference" />
          <NumericInput size={Size.Medium} label={t('payment.new.amount')} kind="currency" value={amountCents} onChange={setAmountCents} required name="paymentAmount" />
          {/* 💰 The surcharge rate is Money out, banking, payments, expenses' Settings row, so the figure is typed until that piece lands (decision 0239). */}
          {method === 'Card' ? (
            <div data-payment-surcharge="true">
              <NumericInput size={Size.Medium} label={t('payment.new.surcharge')} kind="currency" value={surchargeCents} onChange={setSurchargeCents} name="paymentSurcharge" />
            </div>
          ) : null}
          <p className={styles.hint}>{t('payment.recordedAtSave')}</p>
        </>
      }
      lines={
        settlesInvoices ? (
          <>
            <Checkbox label={t('payment.new.proRata')} checked={proRata} onChange={setProRata} size={Size.Medium} />
            {answer && answer.openInvoices.length === 0 ? <p className={styles.hint} data-payment-no-invoices="true">{t('payment.lines.none')}</p> : null}
            {/* 🔨 A pro-rata split is the server's own, so the per-invoice boxes are not drawn beside it and cannot disagree with it. */}
            {!proRata && autoApply && spreadable.length > 0 ? <p className={styles.hint} data-payment-oldest-first="true">{t('payment.new.oldestFirst')}</p> : null}
            {proRata ? null : (
              <ul className={lineStyles.lineList} data-payment-lines={answer?.openInvoices.length ?? 0}>
                {(answer?.openInvoices ?? []).map((row) => (
                  <li key={row.id} className={lineStyles.row} data-payment-line={row.id} data-payment-line-status={row.status} data-payment-line-applied={shown[row.id] ?? 0}>
                    <span className={lineStyles.lineMain}>
                      <span className={lineStyles.rowName}>{row.invoiceNumber ?? ''}</span>
                      <span className={lineStyles.rowNote}>
                        <Money cents={row.openBalanceCents} currencyCode={currencyCode} />{' '}
                        {line(
                          t('payment.lines.owes'),
                          '·',
                          `${t('payment.lines.due')} ${row.dueDate}`,
                          // 🔨 A written-off invoice says what pressing this will reverse, rather than reading as an ordinary unpaid one (decision 0197).
                          row.status === 'WrittenOff' ? `· ${t('payment.lines.writtenOff')}` : null,
                        )}
                      </span>
                    </span>
                    <span className={lineStyles.rowMiddle}><Money cents={row.totalCents} currencyCode={currencyCode} /></span>
                    <span className={lineStyles.rowAmount}>
                      <NumericInput size={Size.Medium}
                        label={line(t('payment.lines.apply'), '·', row.invoiceNumber ?? row.id)}
                        labelHidden
                        kind="currency"
                        value={shown[row.id] ?? null}
                        onChange={(next) => {
                          // 🔨 A typed figure takes the spread over as it stands, so the other boxes keep what they showed.
                          setApplied({ ...shown, [row.id]: next ?? 0 });
                          setAutoApply(false);
                        }}
                        name={`apply-${row.id}`}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/* 🔒 The account's held credit, each note with what it holds and a box that opens empty (decision 2100). */}
            {offersCredit ? (
              <section data-payment-held-credits={heldCredits.length}>
                <p className={lineStyles.rowName}>{t('payment.heldCredit.title')}</p>
                <p className={styles.hint}>{t('payment.heldCredit.hint')}</p>
                <ul className={lineStyles.lineList}>
                  {heldCredits.map((row) => (
                    <li key={row.creditNoteId} className={lineStyles.row} data-payment-held-credit={row.creditNoteId} data-payment-held-credit-used={creditUsed[row.creditNoteId] ?? 0}>
                      <span className={lineStyles.lineMain}>
                        <span className={lineStyles.rowName}>{row.creditNoteNumber}</span>
                        <span className={lineStyles.rowNote}>
                          {t('payment.heldCredit.holds')} <Money cents={row.heldCents} currencyCode={currencyCode} />
                        </span>
                      </span>
                      <span className={lineStyles.rowAmount}>
                        <NumericInput size={Size.Medium}
                          label={line(t('payment.heldCredit.use'), '·', row.creditNoteNumber)}
                          labelHidden
                          kind="currency"
                          value={creditUsed[row.creditNoteId] ?? null}
                          // 🔨 The box is bounded at what the note holds, so it never asks the server for credit that is not there.
                          onChange={(next) => setCreditUsed({ ...creditUsed, [row.creditNoteId]: Math.min(row.heldCents, Math.max(0, next ?? 0)) })}
                          name={`held-${row.creditNoteId}`}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
                <p className={lineStyles.total} data-payment-credit-used={creditTotal}><>{line(t('payment.heldCredit.used'))} <Money cents={creditTotal} currencyCode={currencyCode} /></></p>
              </section>
            ) : null}
            {/* 🔨 The figure is derived from the boxes, so it is not drawn where the boxes are hidden: two figures on one screen that cannot agree is worse than one. */}
            {proRata ? (
              <p className={styles.hint} data-payment-pro-rata="true">{t('payment.new.proRataUnapplied')}</p>
            ) : (
              <>
                <p className={lineStyles.total} data-payment-unapplied={unapplied}><>{line(t('payment.new.unapplied'))} <Money cents={unapplied} currencyCode={currencyCode} /></></p>
                <p className={styles.hint}>{t('payment.new.unappliedHint')}</p>
              </>
            )}
          </>
        ) : (
          <p className={styles.hint} data-payment-sent="true">{t('payment.new.sentNoInvoices')}</p>
        )
      }
    />
    {addNew.host}
    </>
  );
}
